import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-hot-toast";
import { CREATE_ITEM_API, GET_ID_ITEM_API, GET_ITEM_API, SOFT_DELETE_ITEM_API, SUBMIT_ITEM_API, UPDATE_ITEM_API, UPLOAD_ITEM_API } from "src/config/apiConfig";
import { ItemRequest, SubmitRequest } from "src/models/master_data/item/item.dto";
import { RootState } from "../store";
import { useSelector } from "react-redux";


export const fetchMsItemsThunk = createAsyncThunk(
    'msItems/fetchMsItemsThunk',
    async ({ pageNumber, pageSize }: { pageNumber: number, pageSize: number }, thunkAPI) => {
        try {        
            const accessToken = localStorage.getItem('userToken');

            // Set authorization header
            const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

            // Make the API request
            const response = await axios.get(GET_ITEM_API, {
                params: {
                    pageNumber: pageNumber,
                    pageSize: pageSize,
                },
                headers,
            });
            if (response.status === 200) {
                return { data: response.data };
            } else {
                toast.error('Failed to fetch items');
                return thunkAPI.rejectWithValue('Failed to fetch items');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while fetching items');
            console.log(error)
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const fetchByIdMsItemThunk = createAsyncThunk(
    'msItems/fetchByIdMsItemThunk',
    async (itemId: string, thunkAPI) => {
        try {
            const response = await axios.put(`${GET_ID_ITEM_API}/${itemId}`);
            if (response.status === 200) {
                return { data: response.data };
            } else {
                toast.error('Failed to fetch item');
                return thunkAPI.rejectWithValue('Failed to fetch item');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while fetching the item');
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const createMsItemThunk = createAsyncThunk(
    'msItems/createMsItemThunk',
    async (itemData: ItemRequest, thunkAPI) => {
        try {
            const response = await axios.post(CREATE_ITEM_API, itemData);
            if (response.status === 200 || response.status === 201) {
                toast.success('Item created successfully');
                return { data: response.data };
            } else {
                toast.error('Failed to create item');
                return thunkAPI.rejectWithValue('Failed to create item');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while creating the item');
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const updateMsItemThunk = createAsyncThunk(
    'msItems/updateMsItemThunk',
    async ({ id, ...itemData }: { id: string } & ItemRequest, thunkAPI) => {
        try {
            const response = await axios.put(`${UPDATE_ITEM_API}/${id}`, itemData);

            if (response.status === 200) {
                toast.success('Item updated successfully');
                return { data: response.data };
            } else {
                toast.error('Failed to update item');
                return thunkAPI.rejectWithValue('Failed to update item');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while updating the item');
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const softDeleteMsItemThunk = createAsyncThunk(
    'msItems/softDeleteMsItemThunk',
    async (itemId: string, thunkAPI) => {
        try {
            const response = await axios.put(`${SOFT_DELETE_ITEM_API}/${itemId}`);
            if (response.status === 200) {
                toast.success('Item deleted successfully');
                return { data: response.data };
            } else {
                toast.error('Failed to delete item');
                return thunkAPI.rejectWithValue('Failed to delete item');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while deleting the item');
            return thunkAPI.rejectWithValue(error);
        }
    }
);



export const uploadMsItemThunk = createAsyncThunk(
    'msItems/uploadMsItemThunk',
    async (file: File, thunkAPI) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(UPLOAD_ITEM_API, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (response.status === 200) {
                toast.success('File uploaded successfully');
                return { data: response.data };
            } else {
                toast.error('Failed to upload file');
                return thunkAPI.rejectWithValue('Failed to upload file');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while uploading the file');
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const submitMsItemThunk = createAsyncThunk(
    'msItems/submitMsItemThunk',
    async (submitData: SubmitRequest, thunkAPI) => {
        try {
            const response = await axios.post(SUBMIT_ITEM_API, submitData);
            if (response.status === 200) {
                toast.success('Items submitted successfully');
                return { data: response.data };
            } else {
                toast.error('Failed to submit items');
                return thunkAPI.rejectWithValue('Failed to submit items');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.errorMessage || 'An error occurred while submitting items');
            return thunkAPI.rejectWithValue(error);
        }
    }
);